import { toast } from "react-toastify";

import { AxiosResponse } from "axios";
import { ChartData } from "chart.js";
import { convertDateRange, normalizeGroupByCharacteristic, toFormData } from "utils";
import downloadFileAxios from "utils/download-file-axios";
import assignIdsToRows from "utils/reports-table-ids";

import { ReportsAction } from "..";
import { indexNames } from "../../../constants/reports";
import {
  AltiusRes,
  BulkChartConfig,
  BulkPieChartsResponse,
  Chart,
  ChartType,
  CubicPageType,
  CurrencyType,
  DataFreshnessCardData,
  DataFreshnessTimeRange,
  DateRange,
  DateRangeValue,
  DealBase,
  DealGraph,
  ExchangeMethod,
  FeesMethodType,
  FlatReportsTableData,
  GetBarGraphResponse,
  GetPieChartData,
  GetReportsDataResponse,
  GroupBy,
  IAxis,
  ICubic,
  ICubicData,
  IReports,
  ReportContext,
  ReportFilters,
  ReportGeneratorTemplate,
  ReportGeneratorTemplateListItem,
  ReportGeneratorTemplateSavePayload,
  ReportsFileDataType,
  ReportsMeta,
  ReportsQueries,
  ReportTableColumnBy,
  ReportTableData,
  SearchImageResult,
  TelescopeTable
} from "../../types";
import type {
  BenchmarkComparisonCardData,
  ExposureCardData,
  MacroBriefAvailableIndicator,
  MacroBriefCardData,
  PerformanceAttributionCardData
} from "../../types/reports/report-generator";
import { AsyncActions, AsyncActionsParams } from "../base";

export interface IReportsActions {
  getMetaAction: (normalizeQueries?: boolean) => Promise<void>;
  getComparisonTableAction: (
    data: Pick<
      ReportsQueries,
      | "dateRange"
      | "secondaryDateRange"
      | "measures"
      | "groupBy"
      | "calculatedFields"
      | "indexName"
      | "comparisonColumns"
      | "rowBy"
      | "currency"
      | "exchangeMethod"
      | "filters"
      | "use_accounting_date"
    >
  ) => Promise<void>;
  getReportTableAction: (
    data: Pick<
      ReportsQueries,
      | "dateRange"
      | "measures"
      | "groupBy"
      | "columnBy"
      | "calculatedFields"
      | "indexName"
      | "rowBy"
      | "currency"
      | "originalCurrency"
      | "characteristics"
      | "exchangeMethod"
      | "filters"
      | "use_accounting_date"
    > & {
      columns: ReportsQueries["reportTableColumns"];
      disableDispatch?: boolean;
      force_navs_date?: boolean;
      show_count_rows?: boolean;
      processKey?: string;
    }
  ) => Promise<GetReportsDataResponse>;
  uploadFileAction: (file: File, dataType: ReportsFileDataType) => Promise<void>;
  setQueriesAction: (queries: Partial<ReportsQueries>) => void;
  replaceQueriesAction: (data: {
    queries: Partial<ReportsQueries>;
    options?: Partial<{
      ignoreCurrency: boolean;
    }>;
  }) => void;
  getPieChartAction: (queries: GetPieChartData) => Promise<Chart>;
  getBulkPieChartsAction: (
    queries: Pick<
      ReportsQueries,
      "filters" | "graphDataFormation" | "dateRange" | "currency" | "exchangeMethod" | "use_accounting_date"
    >
  ) => Promise<BulkPieChartsResponse>;
  setGraphDataFormatAction: (data: {
    key: string;
    measure?: string;
    groupBy?: string;
    chartType?: ChartType;
    xAxis?: IAxis;
    yAxis?: IAxis;
  }) => void;
  getMeasureGraphAction: (
    data: {
      withinDeal?: boolean;
      allowCumulative?: boolean;
    } & Pick<
      ReportsQueries,
      | "filters"
      | "dateRange"
      | "mainParameter"
      | "secondaryParameter"
      | "interval"
      | "currency"
      | "exchangeMethod"
      | "cumulative"
      | "use_accounting_date"
      | "groupByGraph"
      | "columnType"
    >
  ) => Promise<DealGraph>;

  getCubicAction: (
    data: {
      cubics: ICubic;
      pageType: CubicPageType;
      processKey?: string;
    } & Pick<ReportsQueries, "filters" | "exchangeMethod" | "dateRange" | "currency" | "use_accounting_date">
  ) => Promise<ICubicData>;
  getBenchmarkCubicAction: (
    data: {
      cubics: ICubic;
      pageType: CubicPageType;
    } & Pick<ReportsQueries, "dateRange" | "filters" | "exchangeMethod" | "currency">
  ) => Promise<ICubicData>;
  getBarGraphAction: (
    data: Pick<ReportsQueries, "filters" | "currency" | "exchangeMethod" | "dateRange">
  ) => Promise<GetBarGraphResponse>;
  clearTableDataAction: () => void;
  getCustomerAction: () => Promise<AxiosResponse<string>>;
  toggleExpandRowAction: (rowId: string) => void;
  getRawTableAction: (
    data: Pick<
      ReportsQueries,
      | "filters"
      | "dateRange"
      | "measures"
      | "characteristics"
      | "currency"
      | "exchangeMethod"
      | "calculatedFields"
      | "filteredPortfolio"
      | "use_accounting_date"
    >
  ) => Promise<void>;
  getBridgeDataAction: (
    data: Pick<ReportsQueries, "filters" | "dateRange" | "currency" | "exchangeMethod" | "use_accounting_date">
  ) => Promise<void>;
  getRiskMetricsAction: (
    data: Pick<
      ReportsQueries,
      | "dateRange"
      | "measures"
      | "calculatedFields"
      | "currency"
      | "characteristics"
      | "exchangeMethod"
      | "filteredPortfolio"
      | "filters"
      | "use_accounting_date"
    > & {
      dealTitles: string[];
      indexName?: ReportsQueries["indexName"];
      force_navs_date?: boolean;
    }
  ) => Promise<ReportTableData>;
  getExternalMeasuresAction: () => Promise<string[]>;
  getExternalReportsAction: () => Promise<Array<{ id: number; name: string; order: number }>>;
  getExternalAssetTypesAction: () => Promise<Array<{ id: number; name: string }>>;
  getReportAction: (data: {
    id: number;
    date: string;
    deal_ids: number[];
    parameters: string[];
    asset_type_id?: number;
  }) => Promise<void>;
  getIrrCalculationsAction: (
    data: Pick<ReportsQueries, "filters" | "dateRange" | "currency" | "exchangeMethod" | "use_accounting_date">
  ) => Promise<void>;
  getOverviewTableAction: (
    data: Pick<
      ReportsQueries,
      | "dateRange"
      | "measures"
      | "calculatedFields"
      | "currency"
      | "characteristics"
      | "exchangeMethod"
      | "filteredPortfolio"
      | "filters"
      | "use_accounting_date"
    >
  ) => Promise<void>;
  exportMeasureTableAction: (
    data: Pick<
      ReportsQueries,
      | "dateRange"
      | "secondaryDateRange"
      | "measures"
      | "groupBy"
      | "calculatedFields"
      | "rowBy"
      | "currency"
      | "originalCurrency"
      | "characteristics"
      | "dealTableRowBy"
      | "dealTableGroupBy"
      | "dealTableColumnBy"
      | "exchangeMethod"
      | "filters"
      | "use_accounting_date"
      | "indexName"
    > & { columns: ReportsQueries["reportTableColumns"] }
  ) => Promise<void>;
  refetchReportAction: (data: IReports["refetchReports"]) => void;
  getFilters: (filters: ReportFilters) => Promise<Record<string, Array<number | string | boolean>>>;
  setIgnoreDefaultView: (value: boolean) => void;

  // "any" should be replaced with real interface according to API
  calcManageFeesAction: (data: {
    deal_id: number;
    method: FeesMethodType;
    start_date: string | null;
    end_date: string | null;

    commitment_fees_inv?: number | null;
    cost_fees_inv?: number | null;
    commitment_fees_post?: number | null;
    cost_fees_post?: number | null;
    differential_data?: { year: number; rate: number; type: string }[];
  }) => Promise<void>;

  calcManageInterestsAction: (data: {
    deal_id: number;
    hurdle_1: number | null;
    catch_up_1: number | null;
    carry_1: number | null;
  }) => Promise<void>;

  sendBenchmarkEmailAction: (data: { partner_name: string; group_name: string }) => Promise<TelescopeTable>;
  getBenchmarkTelescopeTable: () => Promise<TelescopeTable>;
  setIsHedgeFundsAction: (value: boolean) => void;
  getHedgeFundTableActon: (
    queries: Pick<
      ReportsQueries,
      "filters" | "groupBy" | "use_accounting_date" | "rowBy" | "currency" | "exchangeMethod" | "originalCurrency"
    >
  ) => Promise<ReportTableData>;
  setCubicAction: (data: {
    cubicType: keyof Pick<ReportsQueries, "cubicDeal" | "cubicSnapshot">;
    cubicKey: string;
    filter: string;
    title: string;
  }) => void;

  getFlatDataAction: (
    data: Pick<ReportsQueries, "dateRange" | "currency" | "exchangeMethod" | "groupBy" | "filters"> & {
      rowBy?: string;
      calculatedFields?: string[];
      columns: string[];
    }
  ) => Promise<FlatReportsTableData>;

  getInlineMeasureDataAction: (data: {
    selectedOptions: { type: string; option: string }[];
    queries: {
      dateRange: DateRangeValue;
      currency: CurrencyType;
      filters: ReportFilters;
    };
  }) => Promise<string>;

  getReportsByGroupAction: (groupName: string) => Promise<Report[]>;
  generateReportAction: (data: { reportId: number } & ReportContext) => Promise<void>;

  sendActivityEmailAction: (data: {
    report_type: "summary" | "capital_account_activity";
    date_from: string;
    date_to: string;
    use_create_date: boolean;
    send_me_copy: boolean;
    recipient_ids: number[];
    recipient_emails: string[];
  }) => Promise<void>;

  getAccountRecipientsAction: () => Promise<{ id: number; name: string }[]>;
  askAiAction: (data: { question: string }) => Promise<{ answer: string }>;
  getMeasureSummaryAction: (
    data: {
      parameter: string;
    } & Pick<ReportsQueries, "filters" | "exchangeMethod" | "dateRange" | "currency" | "use_accounting_date">
  ) => Promise<{ parameter: string; average: number; total: number; min: number; max: number }>;

  getUnfundedCommitmentsAction: (
    data: Pick<ReportsQueries, "filters" | "dateRange" | "currency" | "exchangeMethod" | "use_accounting_date"> & {
      processKey?: string;
    }
  ) => Promise<
    Array<{
      name: string;
      committed: number;
      called: number;
      postfix: string;
    }>
  >;

  getCashFlowTimelineAction: (
    data: Pick<ReportsQueries, "filters" | "dateRange" | "currency" | "exchangeMethod" | "use_accounting_date">
  ) => Promise<
    Array<{
      quarter: string;
      called: number;
      distributed: number;
      postfix: string;
    }>
  >;

  getDataFreshnessAction: (data: { timeRange: DataFreshnessTimeRange }) => Promise<DataFreshnessCardData>;

  getPerformanceAttributionAction: (data: {
    sortBy: "irr" | "moic" | "dpi" | "rvpi";
    topN: number;
    dateRange: DateRangeValue;
  }) => Promise<PerformanceAttributionCardData>;

  getMacroBriefIndicatorsAction: () => Promise<MacroBriefAvailableIndicator[]>;
  getMacroBriefAction: (data: { indicators: string[]; include_narrative: boolean }) => Promise<MacroBriefCardData>;

  getMeasureBreakdownAction: (
    data: {
      measure: string;
      dimension: string;
      processKey?: string;
    } & Pick<ReportsQueries, "filters" | "exchangeMethod" | "dateRange" | "currency" | "use_accounting_date">
  ) => Promise<ExposureCardData>;

  getIrrBenchmarkAction: (
    data: {
      benchmarks: string[];
    } & Pick<ReportsQueries, "filters" | "dateRange" | "currency" | "exchangeMethod" | "use_accounting_date">
  ) => Promise<BenchmarkComparisonCardData>;

  // Report Builder Template actions
  getReportTemplatesAction: () => Promise<ReportGeneratorTemplateListItem[]>;
  getReportTemplateAction: (id: number) => Promise<ReportGeneratorTemplate>;
  saveReportTemplateAction: (data: ReportGeneratorTemplateSavePayload) => Promise<ReportGeneratorTemplate>;
  updateReportTemplateAction: (
    id: number,
    data: ReportGeneratorTemplateSavePayload
  ) => Promise<ReportGeneratorTemplate>;
  deleteReportTemplateAction: (id: number) => Promise<void>;
  searchBackgroundImagesAction: (query: string) => Promise<SearchImageResult[]>;
  getCheckMeasureDataAndDealsAction: () => Promise<boolean>;
  getLastViewAction: () => Promise<ReportGeneratorTemplate | null>;
  saveLastViewAction: (data: ReportGeneratorTemplateSavePayload) => Promise<ReportGeneratorTemplate>;
  deleteLastViewAction: () => Promise<void>;
}

interface ReportsActionsParams extends AsyncActionsParams {
  deal: Partial<DealBase>;
}

export class ReportsActions extends AsyncActions<IReportsActions> implements IReportsActions {
  protected readonly deal: Partial<DealBase>;

  constructor({ dispatch, api, deal }: ReportsActionsParams) {
    super({ dispatch, api });
    this.deal = deal;
    this.processes = {};
  }

  private effectiveFilters = ({
    filters,
    exchangeMethod,
    currency
  }: Pick<ReportsQueries, "filters" | "exchangeMethod" | "currency">): ReportFilters => {
    const effectiveFilters = { ...filters };
    Object.keys(effectiveFilters).forEach((filter) => {
      if (effectiveFilters[filter] === undefined || !effectiveFilters[filter].length) {
        delete effectiveFilters[filter];
      }
    });

    if (exchangeMethod === "filter") {
      effectiveFilters["Currency"] = [currency];
    }

    return effectiveFilters;
  };

  private effectiveExchangeDate = (exchangeMethod: ExchangeMethod): ExchangeMethod | undefined => {
    return exchangeMethod === "filter" ? undefined : exchangeMethod;
  };

  // Update Default Queries only once, when rich protected routes otherwise just fetch and save meta
  public getMetaAction = async (normalizeQueries = false): Promise<void> => {
    const {
      data: { data: meta }
    } = await this.api.get<AltiusRes<ReportsMeta>>({
      url: "/api/v0.0.3/reports/fetch-metadata",
      signal: this.addProcess("getMetaAction").signal
    });
    this.removeProcess("getMetaAction");
    this.dispatch({
      type: ReportsAction.GetMetaSuccess,
      payload: {
        meta,
        normalizeQueries
      }
    });
  };

  public getComparisonTableAction: IReportsActions["getComparisonTableAction"] = async ({
    dateRange,
    secondaryDateRange,
    measures,
    groupBy,
    calculatedFields,
    indexName,
    comparisonColumns,
    rowBy,
    currency,
    filters,
    exchangeMethod,
    use_accounting_date
  }) => {
    const controller = this.addProcess("getDataAction");

    const groupByIncludesMeasure = groupBy?.find((group) => group.includes("measure"));
    const comparisonColumnValues = comparisonColumns.map((col) => (col === "Actual" ? undefined : col));

    const effectiveFilters = this.effectiveFilters({ filters, exchangeMethod, currency });
    const effectiveExchangeDate = this.effectiveExchangeDate(exchangeMethod);
    const effectiveDateRange = convertDateRange(dateRange);
    const effectiveSecondaryDateRange = convertDateRange(secondaryDateRange);
    const effectiveIndexName = indexName ? { index: indexName, name: indexNames[indexName] } : undefined;
    const effectiveGroupBy: GroupBy = groupByIncludesMeasure ? groupBy : [["measure"], ...groupBy];
    const effectiveColumns = [
      ...comparisonColumnValues.map((col) => ({ aggregation: col, dateRange: convertDateRange(dateRange) })),
      ...comparisonColumnValues.map((col) => ({ aggregation: col, dateRange: convertDateRange(secondaryDateRange) }))
    ];

    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<GetReportsDataResponse>>({
      url: "/api/v0.0.3/reports/fetch-comparison-data",
      signal: controller.signal,
      data: {
        filters: effectiveFilters,
        dateRange: effectiveDateRange,
        secondaryDateRange: effectiveSecondaryDateRange,
        groupBy: effectiveGroupBy.filter((group) => !group.includes("none")), //we need to remove none option before send to API, it's client only option
        measures,
        columns: effectiveColumns,
        exchangeDate: effectiveExchangeDate,
        calculatedFields,
        rowBy,
        currency,
        indexName: effectiveIndexName,
        use_accounting_date
      }
    });
    this.dispatch({
      type: ReportsAction.GetDataSuccess,
      payload
    });
    this.removeProcess("getDataAction");
  };

  public getReportTableAction: IReportsActions["getReportTableAction"] = async ({
    dateRange,
    measures,
    groupBy,
    columnBy,
    calculatedFields,
    indexName,
    rowBy,
    currency,
    originalCurrency,
    characteristics,
    columns,
    filters,
    exchangeMethod,
    disableDispatch = false,
    use_accounting_date,
    force_navs_date = false,
    show_count_rows,
    processKey
  }) => {
    const key = processKey ? `getReportTableAction_${processKey}` : "getReportTableAction";
    const controller = this.addProcess(key);

    const groupByIncludesMeasure = groupBy?.find((group) => group.includes("measure"));

    const effectiveFilters = this.effectiveFilters({
      filters,
      exchangeMethod,
      currency
    });
    const effectiveExchangeDate = this.effectiveExchangeDate(exchangeMethod);
    const effectiveDateRange = convertDateRange(dateRange);

    let effectiveColumns: string[] | { aggregation?: string; dateRange?: DateRange<string> }[] = [];
    let effectiveOC: boolean = false;
    let effectiveColumnBy: ReportTableColumnBy | undefined;
    let effectiveGroupBy: GroupBy = groupBy;
    const effectiveIndexName = indexName ? { index: indexName, name: indexNames[indexName] } : undefined;
    let url: string;

    if (columnBy === "investor") {
      url = "/fetch-investor-data";
      effectiveGroupBy = groupByIncludesMeasure ? groupBy : [["measure"], ...groupBy];
      effectiveColumnBy = undefined;
    } else if (columnBy === "year" || columnBy === "quarter" || columnBy === "month" || columnBy === "timeline") {
      url = "/fetch-timeline-data";
      effectiveGroupBy = groupByIncludesMeasure ? groupBy : [["measure"], ...groupBy];
      effectiveColumnBy = columnBy;
    } else {
      url = "/fetch-measure-data";
      effectiveColumns = columns.length
        ? columns.map((col) => ({
            ...col,
            dateRange: col.dateRange && convertDateRange(col.dateRange),
            abbreviated: undefined,
            decimal: undefined
          }))
        : [...measures, ...characteristics];
      effectiveColumnBy = undefined;
    }

    effectiveOC = originalCurrency;

    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<GetReportsDataResponse>>({
      url: `/api/v0.0.3/reports${url}`,
      signal: controller.signal,
      data: {
        filters: effectiveFilters,
        dateRange: effectiveDateRange,
        groupBy: effectiveGroupBy.filter((group) => !group.includes("none")), //we need to remove none option before send to API, it's client only option
        measures,
        columns: effectiveColumns,
        columnBy: effectiveColumnBy,
        exchangeDate: effectiveExchangeDate,
        calculatedFields,
        ...(rowBy && { rowBy }),
        currency: effectiveOC ? "ORIGINAL" : currency,
        indexName: effectiveIndexName,
        use_accounting_date,
        ...(force_navs_date && { force_navs_date: true }),
        ...(show_count_rows && { show_count_rows: true })
      }
    });
    if (!disableDispatch) {
      this.dispatch({
        type: ReportsAction.GetDataSuccess,
        payload
      });
    }
    this.removeProcess(key);
    return payload;
  };

  public uploadFileAction = async (file: File, dataType: ReportsFileDataType): Promise<void> => {
    await this.api.post({
      url: "/api/v0.0.3/reports/import",
      data: toFormData({ upload_type: dataType, file }),
      headers: {
        "Content-Type": "multipart/form-data"
      }
    });
    toast(`${dataType} has been uploaded`);
  };

  public setQueriesAction = (queries: Partial<ReportsQueries>): void => {
    this.dispatch({ type: ReportsAction.SetQueries, payload: queries });
  };

  public replaceQueriesAction: IReportsActions["replaceQueriesAction"] = (payload) => {
    this.dispatch({ type: ReportsAction.ReplaceQueries, payload });
  };

  public getPieChartAction = async ({ processKey, ...queries }: GetPieChartData): Promise<Chart> => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<Chart>>({
      url: "/api/v0.0.3/reports/fetch-pie-chart",
      data: queries,
      signal: this.addProcess(`getPieChartAction_${processKey}`).signal
    });
    this.removeProcess(`getPieChartAction_${processKey}`);

    return data;
  };

  public getBulkPieChartsAction = async ({
    filters,
    graphDataFormation,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date
  }: Pick<
    ReportsQueries,
    "filters" | "graphDataFormation" | "dateRange" | "currency" | "exchangeMethod" | "use_accounting_date"
  >): Promise<BulkPieChartsResponse> => {
    const bulkChartsConfig: BulkChartConfig[] = Object.entries(graphDataFormation).map(([name, dataFormation]) => {
      const isCalcField = dataFormation.measure.startsWith("calc-field-");
      return {
        groupBy: [[normalizeGroupByCharacteristic(dataFormation.groupBy)]],
        measures: isCalcField ? [] : [dataFormation.measure],
        calculatedFields: isCalcField ? [dataFormation.measure.split("calc-field-")[1]] : [],
        chartName: name,
        chartType: dataFormation.chartType,
        xAxis:
          dataFormation.xAxis && dataFormation.chartType === "scatter"
            ? {
                name: dataFormation.xAxis.name.startsWith("calc-field-")
                  ? dataFormation.xAxis.name.split("calc-field-")[1]
                  : dataFormation.xAxis.name,
                type: dataFormation.xAxis.name.startsWith("calc-field-") ? "calc" : "measure"
              }
            : undefined,
        yAxis:
          dataFormation.yAxis && dataFormation.chartType === "scatter"
            ? {
                name: dataFormation.yAxis.name.startsWith("calc-field-")
                  ? dataFormation.yAxis.name.split("calc-field-")[1]
                  : dataFormation.yAxis.name,
                type: dataFormation.yAxis.name.startsWith("calc-field-") ? "calc" : "measure"
              }
            : undefined
      };
    });

    const {
      data: { data }
    } = await this.api.post<AltiusRes<BulkPieChartsResponse>>({
      url: "/api/v0.0.3/reports/fetch-bulk-pie-charts",
      data: {
        charts: bulkChartsConfig,
        pageType: "chartSnapshot",
        use_accounting_date: use_accounting_date,
        filters: this.effectiveFilters({ filters, exchangeMethod, currency }),
        dateRange: convertDateRange(dateRange),
        currency: currency,
        exchangeDate: this.effectiveExchangeDate(exchangeMethod)
      },
      signal: this.addProcess("getBulkPieChartsAction").signal
    });
    this.removeProcess("getBulkPieChartsAction");

    return data;
  };

  public setGraphDataFormatAction = (data: {
    key: string;
    measure?: string;
    groupBy?: string;
    chartType?: ChartType;
  }): void => {
    this.dispatch({ type: ReportsAction.SetGraphFormat, payload: data });
  };

  public getMeasureGraphAction: IReportsActions["getMeasureGraphAction"] = async ({
    withinDeal = false,
    allowCumulative = false,
    filters,
    dateRange,
    mainParameter,
    secondaryParameter,
    interval,
    currency,
    exchangeMethod,
    cumulative,
    use_accounting_date,
    groupByGraph,
    columnType
  }) => {
    const { id, title } = this.deal;
    const {
      data: { data }
    } = await this.api.post<AltiusRes<DealGraph>>({
      url: "/api/v0.0.3/reports/fetch-deal-graph",
      signal: this.addProcess("getMeasureGraphAction").signal,
      data: {
        filters:
          withinDeal && id ? { deal_name: [title] } : this.effectiveFilters({ exchangeMethod, filters, currency }),
        dateRange: convertDateRange(dateRange),
        mainParameter,
        secondaryParameter: secondaryParameter ?? null,
        interval,
        currency,
        exchangeDate: this.effectiveExchangeDate(exchangeMethod),
        cumulative: allowCumulative && cumulative,
        use_accounting_date,
        groupBy: groupByGraph ? [groupByGraph] : null,
        columnType: columnType
      }
    });
    this.removeProcess("getMeasureGraphAction");
    return data;
  };

  public getCubicAction: IReportsActions["getCubicAction"] = async ({
    cubics,
    pageType,
    filters,
    exchangeMethod,
    currency,
    dateRange,
    use_accounting_date,
    processKey
  }) => {
    const key = processKey ? `getCubicAction_${processKey}` : "getCubicAction";
    const {
      data: { data }
    } = await this.api.post<AltiusRes<ICubicData>>({
      url: "/api/v0.0.3/reports/fetch-cubic-data",
      signal: this.addProcess(key).signal,
      data: {
        filters: this.effectiveFilters({ filters, exchangeMethod, currency }),
        dateRange: convertDateRange(dateRange),
        currency: currency,
        exchangeDate: this.effectiveExchangeDate(exchangeMethod),
        pageType,
        use_accounting_date,
        ...cubics
      }
    });
    this.removeProcess(key);
    // this.dispatch({ type: ReportsAction.SetCubicFormat, payload: cubicData });
    return data;
  };

  public getBenchmarkCubicAction: IReportsActions["getBenchmarkCubicAction"] = async ({
    cubics,
    pageType,
    filters,
    exchangeMethod,
    currency,
    dateRange
  }) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<ICubicData>>({
      url: "/api/v0.0.3/reports/fetch-cubic-data",
      signal: this.addProcess("getBenchmarkCubicAction").signal,
      data: {
        filters: this.effectiveFilters({ filters, exchangeMethod, currency }),
        dateRange: convertDateRange(dateRange),
        currency: currency,
        exchangeDate: this.effectiveExchangeDate(exchangeMethod),
        pageType,
        ...cubics
      }
    });
    this.removeProcess("getBenchmarkCubicAction");
    return data;
  };

  public getBarGraphAction: IReportsActions["getBarGraphAction"] = async ({
    filters,
    dateRange,
    currency,
    exchangeMethod
  }) => {
    const { data } = await this.api.post<AltiusRes<ChartData<"bar">>>({
      url: "/api/v0.0.3/reports/fetch-bar-graph",
      data: {
        filters: this.effectiveFilters({
          filters,
          exchangeMethod,
          currency
        }),
        dateRange: convertDateRange(dateRange),
        exchangeDate: this.effectiveExchangeDate(exchangeMethod),
        currency: currency
      }
    });
    return data;
  };

  public clearTableDataAction = (): void => {
    this.dispatch({ type: ReportsAction.clearTableData, payload: null });
  };

  public getCustomerAction = (): Promise<AxiosResponse<string>> => {
    return this.api.get({ url: "/reports/getCustomer" });
  };

  public toggleExpandRowAction = (rowId: string): void => {
    this.dispatch({ type: ReportsAction.ToggleExpandRow, payload: rowId });
  };

  public getRawTableAction: IReportsActions["getRawTableAction"] = async ({
    filters,
    dateRange,
    measures,
    characteristics,
    currency,
    exchangeMethod,
    calculatedFields,
    filteredPortfolio,
    use_accounting_date
  }) => {
    const { id, title } = this.deal;

    return this.api
      .post({
        url: "/api/v0.0.3/reports/export/raw-data",
        data: {
          filters:
            id && title && !filteredPortfolio
              ? {
                  deal_name: [title]
                }
              : filters,
          dateRange,
          measures: [...measures, ...characteristics],
          currency,
          exchangeDate: exchangeMethod,
          calculatedFields,
          use_accounting_date
        },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public getBridgeDataAction: IReportsActions["getBridgeDataAction"] = async ({
    filters,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date
  }) => {
    const { id, title } = this.deal;
    return this.api
      .post({
        url: "/api/v0.0.3/reports/export/bridge-data",
        data: {
          filters: id && title ? { deal_name: [title] } : filters,
          dateRange,
          currency,
          exchangeDate: exchangeMethod,
          use_accounting_date
        },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public getRiskMetricsAction: IReportsActions["getRiskMetricsAction"] = async ({
    dateRange,
    measures,
    calculatedFields,
    currency,
    characteristics,
    exchangeMethod,
    filteredPortfolio,
    filters,
    dealTitles,
    indexName,
    use_accounting_date,
    force_navs_date = false
  }) => {
    const { id, title } = this.deal;
    const effectiveDateRange = convertDateRange(dateRange);

    // if (!deal || !filters["Deal Name"].length || !calculatedFields.length) {
    //   throw "Exit, no selected values";
    // }

    if (!id && !title) {
      throw "Exit, no selected values";
    }

    const {
      data: {
        data: { tableData }
      }
    } = await this.api.post<AltiusRes<{ tableData: ReportTableData }>>({
      url: "/api/v0.0.3/reports/deal-view-calculated-values",
      signal: this.addProcess("getRiskMetricsAction").signal,
      data: {
        filters: { deal_name: dealTitles },
        dateRange: effectiveDateRange,
        use_accounting_date,
        calculatedFields,
        rowBy: "investor",
        currency,
        columns: [...measures, ...characteristics],
        secondaryFilters: this.effectiveFilters({ filters, exchangeMethod, currency }),
        exchangeDate: exchangeMethod,
        filteredPortfolio,
        ...(indexName && { indexName: { index: indexName, name: indexNames[indexName] } }),
        ...(force_navs_date && { force_navs_date: true })
      }
    });
    this.removeProcess("getRiskMetricsAction");
    tableData && assignIdsToRows("calc-values", tableData.rows);
    return tableData;
  };

  public getExternalMeasuresAction: IReportsActions["getExternalMeasuresAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<string[]>>({
      url: "/api/v0.0.3/external/parameters",
      signal: this.addProcess("getExternalMeasuresAction").signal
    });
    this.removeProcess("getExternalMeasuresAction");
    return data;
  };

  public getExternalReportsAction: IReportsActions["getExternalReportsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Array<{ id: number; name: string; order: number }>>>({
      url: "/api/v0.0.3/external/reports"
    });
    this.removeProcess("getExternalReportsAction");
    return data;
  };

  public getExternalAssetTypesAction: IReportsActions["getExternalAssetTypesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<Array<{ id: number; name: string }>>>({
      url: "/api/v0.0.3/external/reports/asset-types",
      signal: this.addProcess("getExternalAssetTypesAction").signal
    });
    this.removeProcess("getExternalAssetTypesAction");
    return data;
  };

  public getReportAction: IReportsActions["getReportAction"] = async ({ id, ...body }) => {
    return this.api
      .post({
        url: `/api/v0.0.3/external/reports/${id}`,
        data: body,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public getIrrCalculationsAction: IReportsActions["getIrrCalculationsAction"] = async ({
    filters,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date
  }) => {
    const { title } = this.deal;

    return this.api
      .post({
        url: "/api/v0.0.3/reports/export/irr-calculations",
        data: {
          filters: {
            ...filters,
            deal_name: [title]
          },
          dateRange,
          // measures: [...measures, ...characteristics],
          currency,
          exchangeDate: exchangeMethod,
          use_accounting_date
        },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public exportMeasureTableAction: IReportsActions["exportMeasureTableAction"] = async ({
    dateRange,
    secondaryDateRange,
    measures,
    groupBy,
    calculatedFields,
    rowBy,
    currency,
    originalCurrency,
    characteristics,
    dealTableRowBy,
    dealTableGroupBy,
    dealTableColumnBy,
    exchangeMethod,
    filters,
    use_accounting_date,
    columns,
    indexName
  }) => {
    const { id, title } = this.deal;

    const effectiveDateRange = convertDateRange(dateRange);
    let effectiveColumns: string[] | { aggregation?: string; dateRange?: DateRange<string> }[] = [];

    effectiveColumns = columns.length
      ? columns.map((col) => ({
          ...col,
          dateRange: col.dateRange && convertDateRange(col.dateRange),
          abbreviated: undefined,
          decimal: undefined
        }))
      : [...measures, ...characteristics];

    if (id && title) {
      const effectiveDateRange = convertDateRange(dateRange);
      let effectiveGroupBy = dealTableGroupBy;
      let effectiveRowBy = dealTableRowBy;

      if (dealTableColumnBy === "year" || dealTableColumnBy === "quarter" || dealTableColumnBy === "month") {
        effectiveGroupBy = dealTableRowBy === "investor" ? [["measureClassLevel1"], ["measure"]] : [["investor"]];
      }

      if (dealTableColumnBy === "measure") {
        effectiveRowBy = "investor";
        effectiveGroupBy = [["date"]];
      }

      const effectiveIndexName = indexName ? { index: indexName, name: indexNames[indexName] } : undefined;

      return this.api
        .post({
          url: "/api/v0.0.3/reports/export/measure-table",
          data: {
            filters: { deal_name: [title] },
            dealView: true,
            groupBy: effectiveGroupBy,
            dateRange: effectiveDateRange,
            columns: effectiveColumns,
            measures,
            currency,
            exchangeDate: this.effectiveExchangeDate(exchangeMethod),
            rowBy: effectiveRowBy,
            calculatedFields,
            use_accounting_date,
            indexName: effectiveIndexName
          },
          responseType: "blob"
        })
        .then(downloadFileAxios);
    }

    const effectiveFilters = this.effectiveFilters({ filters, exchangeMethod, currency });
    const effectiveExchangeDate = this.effectiveExchangeDate(exchangeMethod);
    const effectiveSecondaryDateRange = convertDateRange(secondaryDateRange);
    const effectiveIndexName = indexName ? { index: indexName, name: indexNames[indexName] } : undefined;

    return this.api
      .post({
        url: "/api/v0.0.3/reports/export/measure-table",
        data: {
          filters: effectiveFilters,
          dateRange: effectiveDateRange,
          secondaryDateRange: effectiveSecondaryDateRange,
          groupBy: groupBy.filter((group) => !group.includes("none")), //we need to remove none option before send to API, it's client only option,
          measures: measures,
          columns: effectiveColumns,
          exchangeDate: effectiveExchangeDate,
          calculatedFields,
          rowBy,
          currency: originalCurrency ? "ORIGINAL" : currency,
          use_accounting_date,
          indexName: effectiveIndexName
        },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public refetchReportAction: IReportsActions["refetchReportAction"] = (payload) => {
    this.dispatch({ type: ReportsAction.RefetchReport, payload });
  };

  public getFilters: IReportsActions["getFilters"] = async (body) => {
    const {
      data: { data }
    } = await this.api.post<AltiusRes<ReportFilters>>({
      url: "/api/v0.0.3/reports/get-filters",
      data: {
        filters: body
      },
      signal: this.addProcess("getFilters").signal
    });
    this.removeProcess("getFilters");
    return data;
  };

  public setIgnoreDefaultView: IReportsActions["setIgnoreDefaultView"] = async (payload) => {
    this.dispatch({ type: ReportsAction.SetIgnoreDefaultView, payload });
  };

  public calcManageFeesAction: IReportsActions["calcManageFeesAction"] = async ({ deal_id, ...body }) => {
    await this.api.post({
      url: `/api/v0.0.3/deals/${deal_id}/calc-management-fees`,
      data: body
    });
  };

  public calcManageInterestsAction: IReportsActions["calcManageInterestsAction"] = async ({ deal_id, ...body }) => {
    await this.api.post({
      url: `/api/v0.0.3/deals/${deal_id}/calc-carried-interests`,
      data: body
    });
  };

  public getOverviewTableAction: IReportsActions["getOverviewTableAction"] = async ({
    dateRange,
    measures,
    calculatedFields,
    currency,
    characteristics,
    exchangeMethod,
    filteredPortfolio,
    filters,
    use_accounting_date
  }) => {
    const { title } = this.deal;

    const effectiveDateRange = convertDateRange(dateRange);

    return this.api
      .post({
        url: "/api/v0.0.3/reports/export/overview-table",
        data: {
          filters: { deal_name: [title] },
          dateRange: effectiveDateRange,
          calculatedFields,
          rowBy: "investor",
          currency,
          columns: [...measures, ...characteristics],
          secondaryFilters: this.effectiveFilters({ filters, exchangeMethod, currency }),
          exchangeDate: exchangeMethod,
          filteredPortfolio,
          use_accounting_date
        },
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public sendBenchmarkEmailAction: IReportsActions["sendBenchmarkEmailAction"] = async (data) => {
    const {
      data: {
        data: { tableData }
      }
    } = await this.api.post<AltiusRes<{ tableData: TelescopeTable }>>({
      url: "api/v0.0.3/benchmark/email",
      data
    });
    return tableData;
  };

  public getBenchmarkTelescopeTable = async () => {
    const {
      data: {
        data: { tableData }
      }
    } = await this.api.get<AltiusRes<{ tableData: TelescopeTable }>>({
      url: "/api/v0.0.3/benchmark/telescope"
    });
    return tableData;
  };

  public setIsHedgeFundsAction: IReportsActions["setIsHedgeFundsAction"] = (payload) => {
    this.dispatch({ type: ReportsAction.SetIsHedgeFunds, payload });
  };

  public getHedgeFundTableActon: IReportsActions["getHedgeFundTableActon"] = async (queries) => {
    const { filters, groupBy, use_accounting_date, rowBy, currency, exchangeMethod, originalCurrency } = queries;
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<GetReportsDataResponse>>({
      url: "/api/v0.0.3/reports/fetch-returns-data",
      data: {
        filters,
        dateRange: {},
        use_accounting_date,
        groupBy: groupBy,
        rowBy: rowBy,
        currency: originalCurrency ? "ORIGINAL" : currency,
        exchangeMethod: this.effectiveExchangeDate(exchangeMethod)
      }
    });

    return payload.tableData;
  };

  public setCubicAction: IReportsActions["setCubicAction"] = async (payload) => {
    this.dispatch({ type: ReportsAction.SetCubic, payload });
  };

  public getFlatDataAction: IReportsActions["getFlatDataAction"] = async (data) => {
    const {
      data: {
        data: { tableData }
      }
    } = await this.api.post<AltiusRes<{ tableData: FlatReportsTableData }>>({
      url: "/api/v0.0.3/reports/fetch-flat-data",
      data: { ...data, dateRange: convertDateRange(data.dateRange) }
    });
    return tableData;
  };

  public getInlineMeasureDataAction: IReportsActions["getInlineMeasureDataAction"] = async (data) => {
    const { queries, selectedOptions } = data;

    const effectiveFilters = this.effectiveFilters({
      filters: queries.filters,
      exchangeMethod: "toDate", // we always use toDate for inline measure
      currency: queries.currency
    });

    const effectiveDateRange = convertDateRange(queries.dateRange);

    const measures: string[] = [];
    const calculatedFields: string[] = [];
    const columns: { aggregation?: string; dateRange?: DateRange<string> }[] = [];

    selectedOptions.forEach((option) => {
      if (option.type === "Measures") {
        measures.push(option.option);
      } else if (option.type === "Calculated Fields") {
        calculatedFields.push(option.option);
      }
      columns.push({ aggregation: option.option, dateRange: effectiveDateRange });
    });

    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<string>>({
      url: "/api/v0.0.3/reports/fetch-measure-data",
      data: {
        filters: effectiveFilters,
        dateRange: effectiveDateRange,
        groupBy: [["deal"]], // we always use deal for inline measure
        exchangeDate: "toDate",
        rowBy: "deal",
        currency: queries.currency,
        use_accounting_date: false,
        inline_measure: true,

        measures,
        columns,
        calculatedFields
      }
    });
    return payload;
  };

  /**
   * Get all reports for a report group
   * Returns all reports without context filtering
   */
  public getReportsByGroupAction: IReportsActions["getReportsByGroupAction"] = async (groupName) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<Report[]>>({
      url: `/api/v0.0.3/reports/groups/${groupName}/list`
    });

    return payload;
  };

  /**
   * Generate a specific report
   */
  public generateReportAction: IReportsActions["generateReportAction"] = async ({ reportId, ...body }) => {
    return this.api
      .post({
        url: `/api/v0.0.3/reports/${reportId}/generate`,
        data: body,
        responseType: "blob"
      })
      .then(downloadFileAxios);
  };

  public sendActivityEmailAction: IReportsActions["sendActivityEmailAction"] = async (data) => {
    await this.api.post({
      url: "/api/v0.0.3/reports/activity-email/send",
      data
    });
  };

  public getAccountRecipientsAction: IReportsActions["getAccountRecipientsAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get({
      url: "/api/v0.0.3/reports/activity-email/recipients"
    });
    return payload;
  };

  /**
   * Ask AI a question (for Report Builder AI card)
   */
  public askAiAction: IReportsActions["askAiAction"] = async ({ question }) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<{ answer: string }>>({
      url: "/api/v0.0.3/report-generator/ask-ai",
      data: { question }
    });
    return payload;
  };

  public getMeasureSummaryAction: IReportsActions["getMeasureSummaryAction"] = async ({
    parameter,
    filters,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date
  }) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<{ parameter: string; average: number; total: number; min: number; max: number }>>(
      {
        url: "/api/v0.0.3/report-generator/measure-summary",
        data: {
          parameter,
          filters,
          dateRange: convertDateRange(dateRange),
          currency,
          exchangeDate: this.effectiveExchangeDate(exchangeMethod),
          use_accounting_date
        }
      }
    );
    return payload;
  };

  public getUnfundedCommitmentsAction: IReportsActions["getUnfundedCommitmentsAction"] = async ({
    filters,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date,
    processKey
  }) => {
    const {
      data: { data: payload }
    } = await this.api.post<
      AltiusRes<
        Array<{
          name: string;
          committed: number;
          called: number;
          postfix: string;
        }>
      >
    >({
      url: "/api/v0.0.3/report-generator/unfunded-commitments-by-deal",
      data: {
        filters: this.effectiveFilters({ filters, exchangeMethod, currency }),
        dateRange: convertDateRange(dateRange),
        currency,
        exchangeDate: this.effectiveExchangeDate(exchangeMethod),
        use_accounting_date
      },
      signal: this.addProcess(
        processKey ? `getUnfundedCommitmentsAction_${processKey}` : "getUnfundedCommitmentsAction"
      ).signal
    });
    this.removeProcess(processKey ? `getUnfundedCommitmentsAction_${processKey}` : "getUnfundedCommitmentsAction");
    return payload;
  };

  public getCashFlowTimelineAction: IReportsActions["getCashFlowTimelineAction"] = async ({
    filters,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date
  }) => {
    const {
      data: { data: payload }
    } = await this.api.post<
      AltiusRes<
        Array<{
          quarter: string;
          called: number;
          distributed: number;
          postfix: string;
        }>
      >
    >({
      url: "/api/v0.0.3/report-generator/cashflow-timeline",
      data: {
        filters: this.effectiveFilters({ filters, exchangeMethod, currency }),
        dateRange: convertDateRange(dateRange),
        currency,
        exchangeDate: this.effectiveExchangeDate(exchangeMethod),
        use_accounting_date
      },
      signal: this.addProcess("getCashFlowTimelineAction").signal
    });
    this.removeProcess("getCashFlowTimelineAction");
    return payload;
  };

  public getDataFreshnessAction: IReportsActions["getDataFreshnessAction"] = async ({ timeRange }) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<DataFreshnessCardData>>({
      url: `/api/v0.0.3/report-generator/data-freshness?time_range=${timeRange}`,
      signal: this.addProcess("getDataFreshnessAction").signal
    });
    this.removeProcess("getDataFreshnessAction");
    return payload;
  };

  public getPerformanceAttributionAction: IReportsActions["getPerformanceAttributionAction"] = async ({
    sortBy,
    topN,
    dateRange
  }) => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<PerformanceAttributionCardData>>({
      url: "/api/v0.0.3/report-generator/performance-attribution",
      params: {
        sortBy,
        topN,
        // Ratio measures (IRR/MOIC/…) are currency-independent, so only the date range
        // is applied here — sent as resolved from/to.
        ...convertDateRange(dateRange)
      },
      signal: this.addProcess("getPerformanceAttributionAction").signal
    });
    this.removeProcess("getPerformanceAttributionAction");
    return payload;
  };

  public getMacroBriefIndicatorsAction: IReportsActions["getMacroBriefIndicatorsAction"] = async () => {
    const {
      data: { data: payload }
    } = await this.api.get<AltiusRes<MacroBriefAvailableIndicator[]>>({
      url: "/api/v0.0.3/report-generator/macrobrief/indicators",
      signal: this.addProcess("getMacroBriefIndicatorsAction").signal
    });
    this.removeProcess("getMacroBriefIndicatorsAction");
    return payload;
  };

  public getMacroBriefAction: IReportsActions["getMacroBriefAction"] = async ({ indicators, include_narrative }) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<MacroBriefCardData>>({
      url: "/api/v0.0.3/report-generator/macrobrief",
      data: { indicators, include_narrative },
      signal: this.addProcess("getMacroBriefAction").signal
    });
    this.removeProcess("getMacroBriefAction");
    return payload;
  };

  public getMeasureBreakdownAction: IReportsActions["getMeasureBreakdownAction"] = async ({
    measure,
    dimension,
    filters,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date,
    processKey
  }) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<ExposureCardData>>({
      url: "/api/v0.0.3/report-generator/measure-breakdown",
      data: {
        measure,
        dimension,
        filters: this.effectiveFilters({ filters, exchangeMethod, currency }),
        dateRange: convertDateRange(dateRange),
        currency,
        exchangeDate: this.effectiveExchangeDate(exchangeMethod),
        use_accounting_date
      },
      signal: this.addProcess(processKey ? `getMeasureBreakdownAction_${processKey}` : "getMeasureBreakdownAction")
        .signal
    });
    this.removeProcess(processKey ? `getMeasureBreakdownAction_${processKey}` : "getMeasureBreakdownAction");
    return payload;
  };

  public getIrrBenchmarkAction: IReportsActions["getIrrBenchmarkAction"] = async ({
    benchmarks,
    filters,
    dateRange,
    currency,
    exchangeMethod,
    use_accounting_date
  }) => {
    const {
      data: { data: payload }
    } = await this.api.post<AltiusRes<BenchmarkComparisonCardData>>({
      url: "/api/v0.0.3/report-generator/irr-benchmark",
      data: {
        benchmarks,
        filters: this.effectiveFilters({ filters, exchangeMethod, currency }),
        dateRange: convertDateRange(dateRange),
        currency,
        exchangeDate: this.effectiveExchangeDate(exchangeMethod),
        use_accounting_date
      },
      signal: this.addProcess("getIrrBenchmarkAction").signal
    });
    this.removeProcess("getIrrBenchmarkAction");
    return payload;
  };

  // Report Builder Template actions
  public getReportTemplatesAction: IReportsActions["getReportTemplatesAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ReportGeneratorTemplateListItem[]>>({
      url: "/api/v0.0.3/report-generator/templates",
      params: { template_type: "template" }
    });
    return data;
  };

  public getReportTemplateAction: IReportsActions["getReportTemplateAction"] = async (id) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<ReportGeneratorTemplate>>({
      url: `/api/v0.0.3/report-generator/templates/${id}`
    });
    return data;
  };

  public saveReportTemplateAction: IReportsActions["saveReportTemplateAction"] = async (data) => {
    const formData = toFormData(data);
    const {
      data: { data: result }
    } = await this.api.post<AltiusRes<ReportGeneratorTemplate>>({
      url: "/api/v0.0.3/report-generator/templates",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return result;
  };

  public updateReportTemplateAction: IReportsActions["updateReportTemplateAction"] = async (id, data) => {
    const formData = toFormData({ ...data, _method: "PATCH" });
    const {
      data: { data: result }
    } = await this.api.post<AltiusRes<ReportGeneratorTemplate>>({
      url: `/api/v0.0.3/report-generator/templates/${id}`,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return result;
  };

  public deleteReportTemplateAction: IReportsActions["deleteReportTemplateAction"] = async (id) => {
    await this.api.delete({
      url: `/api/v0.0.3/report-generator/templates/${id}`
    });
  };

  // Last View actions
  public getLastViewAction: IReportsActions["getLastViewAction"] = async () => {
    try {
      const {
        data: { data }
      } = await this.api.get<AltiusRes<ReportGeneratorTemplate>>({
        url: "/api/v0.0.3/report-generator/last-view"
      });
      return data;
    } catch {
      return null;
    }
  };

  public saveLastViewAction: IReportsActions["saveLastViewAction"] = async (data) => {
    const formData = toFormData(data);
    const {
      data: { data: result }
    } = await this.api.post<AltiusRes<ReportGeneratorTemplate>>({
      url: "/api/v0.0.3/report-generator/templates",
      data: formData,
      headers: { "Content-Type": "multipart/form-data" }
    });
    return result;
  };

  public deleteLastViewAction: IReportsActions["deleteLastViewAction"] = async () => {
    await this.api.delete({
      url: "/api/v0.0.3/report-generator/last-view"
    });
  };

  public searchBackgroundImagesAction: IReportsActions["searchBackgroundImagesAction"] = async (query) => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<SearchImageResult[]>>({
      url: `/api/v0.0.3/report-generator/templates/images/search?search_query=${encodeURIComponent(query)}`
    });
    return data;
  };

  public getCheckMeasureDataAndDealsAction: IReportsActions["getCheckMeasureDataAndDealsAction"] = async () => {
    const {
      data: { data }
    } = await this.api.get<AltiusRes<{ exists: boolean }>>({
      url: "/api/v0.0.3/deals/monitoring-data/exists"
    });

    return data.exists;
  };
}
